import type { AnyRouter } from "@tanstack/react-router";
import { getPublicEnv } from "#/modules/util.instrumentation/public-env";

// Skew protection, proactive half (#98): poll /api/version and, once the server reports a
// different build than this tab booted with, turn the next client-side navigation into a
// full page load — the user upgrades at a moment that cannot lose in-page state. The
// vite:preloadError reload (router.tsx) stays as fallback (docs/deploy-runbook.md).
const CHECK_INTERVAL_MS = 5 * 60_000;
const CHECK_DEBOUNCE_MS = 30_000;

export function initVersionCheck(router: AnyRouter) {
  const bootVersion = getPublicEnv().APP_VERSION;
  if (!bootVersion) return; // dev/test builds carry no version

  let stale = false;
  let lastCheckAt = 0;

  const check = async () => {
    if (stale || document.hidden) return;
    if (Date.now() - lastCheckAt < CHECK_DEBOUNCE_MS) return;
    lastCheckAt = Date.now();
    try {
      const res = await fetch("/api/version", { cache: "no-store" });
      if (!res.ok) return;
      const serverVersion = (await res.text()).trim();
      // "Different", not "newer": rollbacks and out-of-order promotes count too.
      if (serverVersion && serverVersion !== bootVersion) stale = true;
    } catch {
      // Offline or mid-deploy blip — next round retries.
    }
  };

  // Tab refocus is the moment stale tabs come back to life (docent returns next morning).
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void check();
  });
  setInterval(() => void check(), CHECK_INTERVAL_MS);

  router.subscribe("onBeforeNavigate", ({ toLocation }) => {
    if (!stale) return;
    // The SPA transition still starts, but the browser navigation supersedes it; if a
    // chunk load it races meanwhile fails, the vite:preloadError reload converges anyway.
    window.location.assign(toLocation.href);
  });
}
